import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useAuth } from './context/AuthContext';
import LockOverlay from './components/LockOverlay';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const groupModifiers = (list) => {
  const groups = {};
  (list || []).forEach(m => {
    const gName = m.modifierGroupAdi || 'Diğer';
    if (!groups[gName]) groups[gName] = [];
    const qty = m.qty || 1;
    groups[gName].push(qty > 1 ? `${qty}x ${m.modifierItemAdi}` : m.modifierItemAdi);
  });
  return groups;
};

const UrunCard = ({ detay }) => (
  <div className="rounded-xl p-3.5 border bg-white dark:bg-surface-800 border-surface-200 dark:border-surface-700">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-lg text-surface-800 dark:text-white">
            {detay.adet}x
          </span>
          <span className="font-semibold text-sm truncate text-surface-700 dark:text-surface-300">
            {detay.urunAdi}
          </span>
        </div>
        {detay.notlar && (
          <p className="text-[11px] text-amber-500 dark:text-amber-400 mt-1 font-medium">
            Not: {detay.notlar}
          </p>
        )}
        {detay.modifiers && detay.modifiers.length > 0 && (() => {
          const groups = groupModifiers(detay.modifiers);
          return (
            <div className="text-[10px] text-surface-400 dark:text-surface-500 mt-1 space-y-0.5">
              {Object.entries(groups).map(([gAdi, items]) => (
                <div key={gAdi} className="flex items-start gap-1">
                  <span className="font-medium text-surface-500 flex-shrink-0">{gAdi}:</span>
                  <span>{items.join(', ')}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  </div>
);

const SiparisKart = ({ siparis }) => (
  <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 overflow-hidden shadow-soft transition-all duration-200">
    <div className="p-4 border-b border-surface-100 dark:border-surface-700 bg-surface-50 dark:bg-surface-700/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-lg text-surface-800 dark:text-white">{siparis.masaNumarasi}</span>
          {siparis.notlar && (
            <span className="text-[10px] text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
              Not var
            </span>
          )}
        </div>
        <span className="text-[11px] text-surface-400 tabular-nums">
          {new Date(siparis.siparisTarihi).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
    <div className="p-4 space-y-2">
      {siparis.siparisDetaylari && siparis.siparisDetaylari.map(d => (
        <UrunCard key={d.detayID} detay={d} />
      ))}
    </div>
  </div>
);

const MutfakPanel = () => {
  const { token, kullanici, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAktifSiparisler();
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/siparis')
      .configureLogging(LogLevel.Information)
      .build();
    connection.on('YeniSiparisAlindi', () => fetchAktifSiparisler());
    connection.on('SiparisGuncellendi', () => fetchAktifSiparisler());
    connection.on('SiparisDurumuDegisti', () => fetchAktifSiparisler());
    connection.on('SiparisKapatildi', () => fetchAktifSiparisler());
    connection.start().catch(e => console.error('SignalR Error:', e));
    const interval = setInterval(fetchAktifSiparisler, 30000);
    return () => { connection.stop(); clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAktifSiparisler = async () => {
    try {
      const res = await fetch(`${API_URL}/siparisler/aktif`, { headers });
      if (res.ok) {
        const data = await res.json();
        let mutfakSiparisleri = data.map(s => ({
          ...s,
          siparisDetaylari: s.siparisDetaylari ? s.siparisDetaylari.filter(d => d.departman === kullanici?.departman) : []
        })).filter(s => s.siparisDetaylari.length > 0);
        mutfakSiparisleri.sort((a, b) => new Date(a.siparisTarihi) - new Date(b.siparisTarihi));
        setSiparisler(mutfakSiparisleri);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-surface-400 font-medium">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      <LockOverlay />
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
                </svg>
              </div>
              <div>
                <h1 className="font-display font-bold text-surface-800 dark:text-white text-sm">{kullanici?.departman || 'Mutfak'} Paneli</h1>
                <p className="text-[10px] text-surface-400 font-medium">{kullanici?.adSoyad || ''}</p>
              </div>
            </div>
            <button onClick={() => { logout(); navigate('/'); }}
              className="px-3.5 py-1.5 text-[11px] font-medium text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {siparisler.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-surface-300 dark:text-surface-600">
            <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium">Şu an sipariş bulunmuyor</p>
            <p className="text-[11px] text-surface-400 dark:text-surface-500 mt-1">Yeni siparişler geldiğinde burada görünecek</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-surface-800 dark:text-white text-base">
                Siparişler ({siparisler.length})
              </h2>
              <button onClick={fetchAktifSiparisler} className="p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {siparisler.map(s => (
                <SiparisKart key={s.siparisID} siparis={s} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MutfakPanel;
